import { useState, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { inventoryApi, supplierApi } from '@/services/api';
import { useCategories, useLocations, useReceiveStock } from '@/hooks/useQueries';
import type { InventoryItem, Category, Location, ReceivedItem, Supplier } from '@/types/index.ts';
import { handleApiError } from '@/utils/errorHandler.ts';
import { getQuantityError, getNameError } from '@/utils/validation.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';
import BarcodeInput from '@/pages/receive/components/BarcodeInput';
import ProductForm from '@/pages/receive/components/ProductForm';
import ReceiptSummary from '@/pages/receive/components/ReceiptSummary';
import ScannerModal from '@/pages/receive/components/ScannerModal';

export default function ReceiveStock() {
  const [barcode, setBarcode] = useState('');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [receivedItems, setReceivedItems] = useState<ReceivedItem[]>([]);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductCategoryId, setNewProductCategoryId] = useState<number | ''>('');
  const [newProductUnit, setNewProductUnit] = useState('');
  const [newProductUnitPrice, setNewProductUnitPrice] = useState('');
  const [newProductCostPrice, setNewProductCostPrice] = useState('');
  const [newProductMarkupType, setNewProductMarkupType] = useState<'$' | '%'>('%');
  const [newProductMarkupValue, setNewProductMarkupValue] = useState('');
  const [newProductMinQuantity, setNewProductMinQuantity] = useState('');
  const [itemsPerUnit, setItemsPerUnit] = useState<number | ''>('');
  // Pricing for existing items
  const [existingCostPrice, setExistingCostPrice] = useState('');
  const [existingUnitPrice, setExistingUnitPrice] = useState('');
  const [existingMarkupType, setExistingMarkupType] = useState<'$' | '%'>('%');
  const [existingMarkupValue, setExistingMarkupValue] = useState('');
  // Batch tracking (FIFO)
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  // Supplier (isNew=true means not yet saved to DB — created on submit)
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierResults, setSupplierResults] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<(Supplier & { isNew?: boolean; pendingPhone?: string; pendingEmail?: string }) | null>(null);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierPhone, setNewSupplierPhone] = useState('');
  const [newSupplierEmail, setNewSupplierEmail] = useState('');
  const [isSearchingSupplier, setIsSearchingSupplier] = useState(false);
  const [notes, setNotes] = useState('');
  const [generatedBarcode, setGeneratedBarcode] = useState<string | null>(null);
  const [isGeneratingBarcode, setIsGeneratingBarcode] = useState(false);
  
  // Loading states
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError] = useState<string | null>(null);
  
  // Data from API
  // Use React Query hooks - only fetch active categories for dropdowns
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories({ per_page: 100, status: 'active' });
  const { data: locationsData, isLoading: locationsLoading } = useLocations({ per_page: 100, type: 'receiving' });
  const receiveStockMutation = useReceiveStock();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const loadingData = categoriesLoading || locationsLoading;
  
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Load initial data
  useEffect(() => {
    // Data is loaded via React Query hooks
  }, []);

  // Auto-focus barcode input when page loads
  useEffect(() => {
    if (!loadingData) {
      barcodeInputRef.current?.focus();
    }
  }, [loadingData]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Update categories and locations from React Query data
  useEffect(() => {
    if (categoriesData) {
      // Handle paginated response: { data: [...], meta: {...} }
      // Or direct array response
      // API already returns only active categories when status='active' is passed
      const categoriesArray = Array.isArray(categoriesData) 
        ? categoriesData 
        : (categoriesData?.data ?? []);
      setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (locationsData !== undefined) {
      let allLocations: Location[] = [];
      if (Array.isArray(locationsData)) {
        allLocations = locationsData;
      } else if (locationsData?.data && Array.isArray(locationsData.data)) {
        allLocations = locationsData.data;
      }
      setLocations(allLocations);
    }
  }, [locationsData]);

  // Supplier search with debounce
  const searchSuppliers = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSupplierResults([]);
      return;
    }
    setIsSearchingSupplier(true);
    try {
      const res = await supplierApi.search({ search: term, per_page: 10 });
      const data = (res as any)?.data?.data ?? (res as any)?.data ?? [];
      setSupplierResults(Array.isArray(data) ? data : []);
    } catch {
      setSupplierResults([]);
    } finally {
      setIsSearchingSupplier(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchSuppliers(supplierSearch), 300);
    return () => clearTimeout(timer);
  }, [supplierSearch, searchSuppliers]);

  const handleSelectSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
    setShowAddSupplier(false);
  };

  const handleCreateSupplier = () => {
    if (!newSupplierName.trim()) return;
    // Store as a pending supplier — will be saved to DB only when the receive is submitted
    setSelectedSupplier({
      id: 0,
      name: newSupplierName.trim(),
      is_active: true,
      isNew: true,
      pendingPhone: newSupplierPhone.trim() || undefined,
      pendingEmail: newSupplierEmail.trim() || undefined,
    });
    setSupplierSearch(newSupplierName.trim());
    setShowAddSupplier(false);
    setShowSupplierDropdown(false);
    setNewSupplierName('');
    setNewSupplierPhone('');
    setNewSupplierEmail('');
  };

  // Markup calculator helpers for existing items
  const recalcExistingSellingPrice = (cost: string, markupVal: string, mType: '$' | '%') => {
    const c = parseFloat(cost);
    const m = parseFloat(markupVal);
    if (!isNaN(c) && c > 0 && !isNaN(m) && m >= 0) {
      const selling = mType === '%' ? c * (1 + m / 100) : c + m;
      setExistingUnitPrice(selling.toFixed(2));
    }
  };

  const handleBarcodeSearch = async () => {
    if (!barcode.trim()) {
      setError('Please enter a barcode');
      return;
    }

    // Preserve barcode as string to maintain leading/trailing zeros
    const barcodeString = barcode.trim();

    try {
      setIsSearching(true);
      setError(null);
      setSuccess(null);

      const response = await inventoryApi.searchByBarcode(barcodeString);
      
      if (response.data) {
        setSelectedItem(response.data);
        setIsNewProduct(false);
        setExistingUnitPrice(String(response.data.unit_price ?? ''));
        setExistingCostPrice(String(response.data.cost_price ?? ''));
        setExistingMarkupValue('');
        setBarcode('');
      }
    } catch (err) {
      const errorDetails = handleApiError(err, 'Barcode Search');
      // Item not found - allow user to add new product
      if (errorDetails.message?.includes('not found') || errorDetails.statusCode === 404) {
        setIsNewProduct(true);
        setSelectedItem({
          barcode: barcodeString, // Use the preserved barcode string
          id: Date.now(),
        } as InventoryItem);
        setNewProductName('');
        setNewProductCategoryId('');
        setNewProductUnit('');
        setNewProductUnitPrice('');
        setNewProductMinQuantity('');
        setBarcode('');
        setGeneratedBarcode(null);
      } else {
        setError(errorDetails.message);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleBarcodeChange = (value: string) => {
    // Only allow numeric input to prevent invalid characters
    const numericValue = value.replace(/\D/g, '');
    setBarcode(numericValue);
    
    // Auto-search when barcode reaches 13 digits (EAN-13 standard)
    // Only search if not already searching to avoid duplicate calls
    if (numericValue.length === 13 && !isSearching) {
      setTimeout(() => {
        handleBarcodeSearch();
      }, 100);
    }
  };

  const handleAddItem = () => {
    // Validate required fields
    if (!selectedItem || !locationId) {
      setError('Please fill all required fields');
      return;
    }

    // Validate quantity using centralized validation
    const quantityError = getQuantityError(quantity);
    if (quantityError) {
      setError(quantityError);
      return;
    }

    // Check if unit requires items per unit specification
    const currentUnit = isNewProduct ? newProductUnit : selectedItem.unit;
    const needsItemsPerUnit = requiresItemsPerUnit(currentUnit || '');
    
    // Validate items per unit if required
    if (needsItemsPerUnit) {
      const itemsPerUnitError = getQuantityError(itemsPerUnit);
      if (itemsPerUnitError) {
        setError(`Please specify how many items are in each ${currentUnit}`);
        return;
      }
    }

    // Validate batch number (required)
    if (!batchNumber.trim()) {
      setError('Batch No. is required');
      return;
    }

    // Validate selling price (auto-calculated from cost + markup)
    const effectiveUnitPrice = isNewProduct ? newProductUnitPrice : existingUnitPrice || String(selectedItem?.unit_price ?? '');
    if (!effectiveUnitPrice || isNaN(parseFloat(effectiveUnitPrice)) || parseFloat(effectiveUnitPrice) <= 0) {
      setError('Please enter the unit cost and markup to calculate a selling price');
      return;
    }

    // Validate new product fields
    if (isNewProduct) {
      const nameError = getNameError(newProductName, 'Product name');
      if (nameError) {
        setError(nameError);
        return;
      }
      
      if (!newProductCategoryId || !newProductUnit) {
        setError('Please select category and unit');
        return;
      }
    } else if (!selectedItem.id) {
      setError('Invalid item selected');
      return;
    }

    const location = locations.find(loc => loc.id === Number(locationId));
    
    // Normalize quantity values
    const normalizedQty = typeof quantity === 'number' ? quantity : Number(quantity);
    
    // Calculate final quantity: if itemsPerUnit is specified, multiply quantity * itemsPerUnit
    const itemsPerUnitValue = needsItemsPerUnit && itemsPerUnit !== ''
      ? (typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit))
      : undefined;
    const finalQuantity = needsItemsPerUnit && itemsPerUnitValue
      ? normalizedQty * itemsPerUnitValue
      : normalizedQty;
    
    const finalUnitPrice = isNewProduct
      ? (newProductUnitPrice ? parseFloat(newProductUnitPrice) : undefined)
      : (existingUnitPrice ? parseFloat(existingUnitPrice) : selectedItem.unit_price);

    const finalCostPrice = isNewProduct
      ? (newProductCostPrice ? parseFloat(newProductCostPrice) : undefined)
      : (existingCostPrice ? parseFloat(existingCostPrice) : undefined);

      const newItem: ReceivedItem = {
      item_id: isNewProduct ? undefined : selectedItem.id,
      barcode: generatedBarcode || selectedItem.barcode,
      name: isNewProduct ? newProductName : selectedItem.name,
      sku: isNewProduct ? undefined : selectedItem.sku,
      category: isNewProduct 
        ? categories.find(cat => cat.id === Number(newProductCategoryId))?.name
        : selectedItem.category?.name,
      category_id: isNewProduct ? Number(newProductCategoryId) : selectedItem.category_id,
      unit: isNewProduct ? newProductUnit : selectedItem.unit,
      quantity: finalQuantity,
      packsBoxes: needsItemsPerUnit ? normalizedQty : undefined,
      itemsPerUnit: itemsPerUnitValue,
      location_id: Number(locationId),
      location: location?.name,
      unit_price: finalUnitPrice,
      cost_price: finalCostPrice,
      batch_number: batchNumber.trim(),
      expiry_date: expiryDate || undefined,
      isNew: isNewProduct,
      timestamp: new Date().toLocaleString(),
      newItemData: isNewProduct ? {
        name: newProductName,
        barcode: generatedBarcode || selectedItem.barcode || '',
        category_id: Number(newProductCategoryId),
        unit: newProductUnit,
        unit_price: newProductUnitPrice ? parseFloat(newProductUnitPrice) : undefined,
        cost_price: newProductCostPrice ? parseFloat(newProductCostPrice) : undefined,
        min_quantity: newProductMinQuantity ? parseInt(newProductMinQuantity) : undefined,
      } : undefined,
    };

    setReceivedItems([...receivedItems, newItem]);
    setSelectedItem(null);
    setIsNewProduct(false);
    setNewProductName('');
    setNewProductCategoryId('');
    setNewProductUnit('');
    setNewProductUnitPrice('');
    setNewProductCostPrice('');
    setNewProductMarkupValue('');
    setNewProductMinQuantity('');
    setExistingCostPrice('');
    setExistingUnitPrice('');
    setExistingMarkupValue('');
    setItemsPerUnit('');
    setQuantity(1);
    setLocationId('');
    setBarcode('');
    setGeneratedBarcode(null);
    setBatchNumber('');
    setExpiryDate('');
    setError(null);
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const handleSubmit = async () => {
    if (receivedItems.length === 0) {
      setError('No items to receive');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      // Format items for API
      const itemsForApi = receivedItems.map(item => {
        interface ApiItem {
          quantity: number;
          item_id?: number;
          new_item?: {
            name: string;
            barcode: string;
            category_id: number;
            unit: string;
            unit_price?: number;
            cost_price?: number;
            min_quantity?: number;
          };
          unit_price?: number;
          cost_price?: number;
          batch_number: string;
          expiry_date?: string;
        }

        const apiItem: ApiItem = {
          quantity: item.quantity,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date || undefined,
        };

        if (item.isNew && item.newItemData) {
          apiItem.new_item = {
            name: item.newItemData.name,
            barcode: item.newItemData.barcode,
            category_id: item.newItemData.category_id,
            unit: item.newItemData.unit,
          };
          if (item.newItemData.unit_price) {
            apiItem.new_item.unit_price = item.newItemData.unit_price;
          }
          if (item.newItemData.cost_price) {
            apiItem.new_item.cost_price = item.newItemData.cost_price;
          }
          if (item.newItemData.min_quantity) {
            apiItem.new_item.min_quantity = item.newItemData.min_quantity;
          }
        } else {
          apiItem.item_id = item.item_id;
        }

        if (item.unit_price) {
          apiItem.unit_price = item.unit_price;
        }
        if (item.cost_price) {
          apiItem.cost_price = item.cost_price;
        }

        return apiItem;
      });

      // If supplier is pending (new, not yet in DB), create it now
      let supplierId = selectedSupplier?.id || undefined;
      if (selectedSupplier?.isNew) {
        try {
          const res = await supplierApi.create({
            name: selectedSupplier.name,
            phone: selectedSupplier.pendingPhone,
            email: selectedSupplier.pendingEmail,
          });
          supplierId = ((res as any)?.data?.id) ?? ((res as any)?.id);
        } catch (err) {
          const e = handleApiError(err, 'Create Supplier');
          setError(e.message);
          setIsSubmitting(false);
          return;
        }
      }

      // Group items by location_id — the backend receive endpoint accepts one
      // location per call, so items destined for different locations require
      // separate submissions.
      const byLocation = new Map<number, typeof itemsForApi>();
      receivedItems.forEach((item, idx) => {
        const loc = item.location_id;
        if (loc == null) return; // should not happen — validated in handleAddItem
        if (!byLocation.has(loc)) byLocation.set(loc, []);
        byLocation.get(loc)!.push(itemsForApi[idx]);
      });

      for (const [loc, locItems] of byLocation) {
        await receiveStockMutation.mutateAsync({
          location_id: loc,
          supplier_id: supplierId || undefined,
          items: locItems,
          notes: notes.trim() || undefined,
        });
      }

      setSuccess(`Successfully received ${receivedItems.length} item(s).`);
      setReceivedItems([]);
      setNotes('');
      setBarcode('');
      setSelectedSupplier(null);
      setSupplierSearch('');
      
      // Auto-dismiss success after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      const errorDetails = handleApiError(err, 'Receive Stock');
      setError(errorDetails.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const units = ['boxes', 'pcs', 'packs', 'bottles', 'rolls', 'units', 'kg', 'g', 'l', 'ml'];

  // Handle barcode scanned from scanner modal
  const handleBarcodeScanned = (scannedBarcode: string) => {
    const cleanBarcode = scannedBarcode.trim();
    
    if (cleanBarcode) {
      setShowScanner(false);
      
      const numericBarcode = cleanBarcode.replace(/\D/g, '');
      setBarcode(numericBarcode);
      
      // Auto-search after state update
      setTimeout(() => {
        if (numericBarcode) {
          handleBarcodeSearchWithValue(numericBarcode);
        }
      }, 100);
    }
  };

  // search with a specific barcode value 
  const handleBarcodeSearchWithValue = async (barcodeValue: string) => {
    if (!barcodeValue.trim()) {
      setError('Please enter a barcode');
      return;
    }

    // Preserve barcode as string to maintain leading/trailing zeros
    const barcodeString = barcodeValue.trim();

    try {
      setIsSearching(true);
      setError(null);
      setSuccess(null);

      const response = await inventoryApi.searchByBarcode(barcodeString);
      
      if (response.data) {
        setSelectedItem(response.data);
        setIsNewProduct(false);
        setExistingUnitPrice(String(response.data.unit_price ?? ''));
        setExistingCostPrice(String(response.data.cost_price ?? ''));
        setExistingMarkupValue('');
        setItemsPerUnit('');
        setBarcode('');
        setGeneratedBarcode(null);
      }
    } catch (err) {
      const errorDetails = handleApiError(err, 'Barcode Search');
      // Item not found - allow user to add new product
      if (errorDetails.message?.includes('not found') || errorDetails.statusCode === 404) {
        setIsNewProduct(true);
        setSelectedItem({
          barcode: barcodeString,
          id: Date.now(),
        } as InventoryItem);
        setNewProductName('');
        setNewProductCategoryId('');
        setNewProductUnit('');
        setNewProductUnitPrice('');
        setNewProductMinQuantity('');
        setItemsPerUnit('');
        setBarcode('');
        setGeneratedBarcode(null);
      } else {
        setError(errorDetails.message);
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Generate unique UPC barcode
  const handleGenerateBarcode = async () => {
    try {
      setIsGeneratingBarcode(true);
      setError(null);
      
      const response = await inventoryApi.generateBarcode();
      
      if (response.data?.barcode) {
        const newBarcode = response.data.barcode;
        setGeneratedBarcode(newBarcode);
        
        // If no item is selected, create a new product entry with the generated barcode
        if (!selectedItem) {
          setIsNewProduct(true);
          setSelectedItem({
            barcode: newBarcode,
            id: Date.now(),
          } as InventoryItem);
          setNewProductName('');
          setNewProductCategoryId('');
          setNewProductUnit('');
          setNewProductUnitPrice('');
          setNewProductMinQuantity('');
        } else {
          // Update selectedItem with the generated barcode
          setSelectedItem({
            ...selectedItem,
            barcode: newBarcode,
          });
        }
      }
    } catch (err) {
      const errorDetails = handleApiError(err, 'Generate Barcode');
      setError(errorDetails.message);
    } finally {
      setIsGeneratingBarcode(false);
    }
  };

  // Print barcode
  const handlePrintBarcode = async () => {
    if (!generatedBarcode) return;
    
    try {
      // Import JsBarcode dynamically
      const JsBarcode = await import('jsbarcode');
      
      // Create a temporary SVG element
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('id', 'barcode-svg');
      
      // Generate barcode with standard/thinner lines
      JsBarcode.default(svg, generatedBarcode, {
        format: 'UPC',
        width: 1,
        height: 60,
        displayValue: true,
        fontSize: 14,
        margin: 5,
        background: '#ffffff',
        lineColor: '#000000',
      });
      
      // Get SVG as string
      const svgString = new XMLSerializer().serializeToString(svg);
      
      // Open print window with barcode
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Please allow popups to print the barcode');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Barcode Print - ${generatedBarcode}</title>
            <style>
              @media print {
                body { 
                  margin: 0; 
                  padding: 20px; 
                }
                @page {
                  margin: 20mm;
                  size: A4;
                }
              }
              body {
                font-family: Arial, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                padding: 20px;
                background: white;
              }
              .barcode-container {
                text-align: center;
                padding: 20px;
              }
              .barcode-heading {
                font-size: 12px;
                color: #000;
                margin-bottom: 15px;
                font-weight: 600;
              }
              .barcode-svg-container {
                margin: 10px 0;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              .barcode-svg-container svg {
                max-width: 100%;
                height: auto;
              }
            </style>
          </head>
          <body>
            <div class="barcode-container">
              <div class="barcode-heading">HIC-Product</div>
              <div class="barcode-svg-container">
                ${svgString}
              </div>
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 250);
              };
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      console.error('Error generating barcode:', err);
      setError('Failed to generate barcode. Please try again.');
    }
  };


  return (
    <>
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
      <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Receive Stock</h1>
          <div className="bg-[#50b3e1]/10 border border-[#50b3e1]/30 rounded p-2 mt-2 inline-block">
            <div className="flex items-center gap-2">
              <i className="ri-information-line text-[#0997a4] text-sm"></i>
              <p className="text-xs text-gray-700">
                <span className="font-medium">Barcode Scanner Ready:</span> Connect USB scanner or use camera. Click barcode field and scan.
              </p>
            </div>
          </div>
        </div>

        {/* Toast Notifications */}
        {error && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className="bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 min-w-[300px] max-w-md">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl mt-0.5 flex-shrink-0"></i>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900">Error</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-600 hover:text-red-800 flex-shrink-0"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 min-w-[300px] max-w-md">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-green-600 text-xl mt-0.5 flex-shrink-0"></i>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">Success</p>
                  <p className="text-sm text-green-700 mt-1">{success}</p>
                </div>
                <button
                  onClick={() => setSuccess(null)}
                  className="text-green-600 hover:text-green-800 flex-shrink-0"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item Details</h3>

            {/* Barcode Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Barcode (EAN-13 / UPC)
                <span className="ml-2 text-xs font-normal text-[#0997a4]">● Scanner Active</span>
              </label>
              <BarcodeInput
                barcode={barcode}
                onBarcodeChange={handleBarcodeChange}
                onSearch={handleBarcodeSearch}
                isSearching={isSearching}
                selectedItem={selectedItem}
                setShowScanner={setShowScanner}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">
                  <i className="ri-flashlight-line"></i> Auto-detects
                </p>
              </div>
            </div>

            {/* Generate Barcode Section */}
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleGenerateBarcode}
                  disabled={isGeneratingBarcode}
                  className="px-4 py-2 bg-[#50b3e1] text-white rounded-lg text-sm font-medium hover:bg-[#3a9bc7] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <i className={`ri-barcode-line ${isGeneratingBarcode ? 'animate-spin' : ''}`}></i>
                  {isGeneratingBarcode ? 'Generating...' : 'Generate Barcode'}
                </button>
                
                {generatedBarcode && (
                  <div className="flex-1 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 mb-1">Generated UPC Barcode</p>
                      <p className="text-sm font-mono font-semibold text-gray-900">{generatedBarcode}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handlePrintBarcode}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium hover:bg-gray-50 transition-colors flex items-center gap-1"
                      title="Print Barcode"
                    >
                      <i className="ri-printer-line"></i>
                      Print
                    </button>
                  </div>
                )}
              </div>
              {generatedBarcode && (
                <p className="text-xs text-gray-500 mt-2">
                  <i className="ri-information-line"></i> Barcode will be saved when you complete the receipt!
                </p>
              )}
            </div>

            {selectedItem && (
              <>
                {/* New Product Alert */}
                {isNewProduct && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg relative">
                    <button
                      onClick={() => {
                        setSelectedItem(null);
                        setIsNewProduct(false);
                        setItemsPerUnit('');
                        setGeneratedBarcode(null);
                        setError(null);
                      }}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-green-600 hover:text-green-800 hover:bg-green-100 rounded-full transition-colors"
                      title="Clear"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                    <div className="flex items-start gap-2 pr-6">
                      <i className="ri-add-circle-line text-green-600 text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-green-900">New Product Detected</p>
                        <p className="text-xs text-green-700 mt-1">
                          Barcode <span className="font-mono font-medium">{selectedItem.barcode}</span> not found. Please enter product details below.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Item Info or New Product Form */}
                {!isNewProduct ? (
                  <>
                    <div className="mb-4 p-4 bg-[#84d0f4]/10 rounded-lg relative">
                      <button
                        onClick={() => {
                          setSelectedItem(null);
                          setIsNewProduct(false);
                          setItemsPerUnit('');
                          setGeneratedBarcode(null);
                          setError(null);
                          barcodeInputRef.current?.focus();
                        }}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Clear item"
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                      <p className="text-sm font-medium text-gray-900 pr-8">{selectedItem.name}</p>
                      <p className="text-xs text-gray-600 mt-1">SKU: {selectedItem.sku || 'N/A'}</p>
                      <p className="text-xs text-gray-600">Barcode: {selectedItem.barcode}</p>
                      <p className="text-xs text-gray-600">Category: {selectedItem.category?.name || 'N/A'}</p>
                      <p className="text-xs text-gray-600">Unit: {selectedItem.unit || 'N/A'}</p>
                    </div>

                    {/* Quantity and Items per Unit - For existing items */}
                    {(() => {
                      const currentUnit = selectedItem?.unit;
                      const requiresItemsPerUnit = ['pack', 'packs', 'box', 'boxes'].includes(currentUnit?.toLowerCase() || '');
                      
                      if (requiresItemsPerUnit) {
                        return (
                          <>
                            {/* Number of boxes/packs - First */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Number of {currentUnit} <span className="text-[#ed1438]">*</span>
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={quantity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow empty or valid positive integers only
                                  if (value === '' || /^\d+$/.test(value)) {
                                    setQuantity(value === '' ? '' : Number(value));
                                  }
                                }}
                                onWheel={(e) => {
                                  // Prevent scroll-to-change
                                  e.currentTarget.blur();
                                }}
                                onKeyDown={(e) => {
                                  // Allow: backspace, delete, tab, escape, enter, and numbers
                                  if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                    (e.keyCode === 65 && e.ctrlKey === true) ||
                                    (e.keyCode === 67 && e.ctrlKey === true) ||
                                    (e.keyCode === 86 && e.ctrlKey === true) ||
                                    (e.keyCode === 88 && e.ctrlKey === true) ||
                                    // Allow: home, end, left, right
                                    (e.keyCode >= 35 && e.keyCode <= 39)) {
                                    return;
                                  }
                                  // Ensure that it is a number and stop the keypress
                                  if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                    e.preventDefault();
                                  }
                                }}
                                placeholder={`Enter number of ${currentUnit}`}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                          />
                            </div>

                            {/* Items per boxes/packs - Immediately after */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Items per {currentUnit} <span className="text-[#ed1438]">*</span>
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={itemsPerUnit}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow empty or valid positive integers only
                                  if (value === '' || /^\d+$/.test(value)) {
                                    setItemsPerUnit(value === '' ? '' : Number(value));
                                  }
                                }}
                                onWheel={(e) => {
                                  // Prevent scroll-to-change
                                  e.currentTarget.blur();
                                }}
                                onKeyDown={(e) => {
                                  // Allow: backspace, delete, tab, escape, enter, and numbers
                                  if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                    (e.keyCode === 65 && e.ctrlKey === true) ||
                                    (e.keyCode === 67 && e.ctrlKey === true) ||
                                    (e.keyCode === 86 && e.ctrlKey === true) ||
                                    (e.keyCode === 88 && e.ctrlKey === true) ||
                                    // Allow: home, end, left, right
                                    (e.keyCode >= 35 && e.keyCode <= 39)) {
                                    return;
                                  }
                                  // Ensure that it is a number and stop the keypress
                                  if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                    e.preventDefault();
                                  }
                                }}
                                placeholder={`How many items in each ${currentUnit}?`}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Total quantity will be: {quantity !== '' && itemsPerUnit !== '' ? `${quantity} × ${itemsPerUnit} = ${(typeof quantity === 'number' ? quantity : Number(quantity)) * (typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit))}` : 'Quantity × Items per unit'}
                              </p>
                            </div>
                          </>
                        );
                      } else {
                        // Regular quantity field for non-pack/box units
                        return (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quantity <span className="text-[#ed1438]">*</span>
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={quantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow empty or valid positive integers only
                                if (value === '' || /^\d+$/.test(value)) {
                                  setQuantity(value === '' ? '' : Number(value));
                                }
                              }}
                              onWheel={(e) => {
                                // Prevent scroll-to-change
                                e.currentTarget.blur();
                              }}
                              onKeyDown={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, and numbers
                                if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                                  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                  (e.keyCode === 65 && e.ctrlKey === true) ||
                                  (e.keyCode === 67 && e.ctrlKey === true) ||
                                  (e.keyCode === 86 && e.ctrlKey === true) ||
                                  (e.keyCode === 88 && e.ctrlKey === true) ||
                                  // Allow: home, end, left, right
                                  (e.keyCode >= 35 && e.keyCode <= 39)) {
                                  return;
                                }
                                // Ensure that it is a number and stop the keypress
                                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                  e.preventDefault();
                                }
                              }}
                              placeholder="Enter quantity"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            />
                          </div>
                        );
                      }
                    })()}

                    {/* Pricing for existing items */}
                    <div className="mb-4 pt-2 border-t border-gray-100 space-y-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pricing</p>

                      {/* Unit Cost */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unit Cost (JMD)
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={existingCostPrice}
                          onChange={(e) => {
                            setExistingCostPrice(e.target.value);
                            recalcExistingSellingPrice(e.target.value, existingMarkupValue, existingMarkupType);
                          }}
                          placeholder="e.g. 50.00"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        />
                        {existingCostPrice && quantity && (
                          <p className="text-xs text-gray-500 mt-1">
                            Total purchase cost:{' '}
                            <span className="font-semibold text-gray-700">
                              JMD {(parseFloat(existingCostPrice) * (typeof quantity === 'number' ? quantity : Number(quantity))).toLocaleString('en-JM', { minimumFractionDigits: 2 })}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Markup */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Markup <span className="text-[#ed1438]">*</span>
                        </label>
                        <div className="flex gap-2">
                          <div className="flex rounded-lg overflow-hidden border border-gray-300">
                            {(['$', '%'] as const).map((t) => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => {
                                  setExistingMarkupType(t);
                                  recalcExistingSellingPrice(existingCostPrice, existingMarkupValue, t);
                                }}
                                className={`px-3 py-2 text-sm font-medium transition-colors ${existingMarkupType === t ? 'bg-[#0997a4] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={existingMarkupValue}
                            onChange={(e) => {
                              setExistingMarkupValue(e.target.value);
                              recalcExistingSellingPrice(existingCostPrice, e.target.value, existingMarkupType);
                            }}
                            placeholder={existingMarkupType === '%' ? 'e.g. 40' : 'e.g. 20.00'}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                          />
                        </div>
                      </div>

                      {/* Selling Price — read-only */}
                      <p className="text-sm text-gray-600">
                        Selling Price:{' '}
                        {existingUnitPrice && parseFloat(existingUnitPrice) > 0
                          ? <span className="font-semibold text-gray-900">JMD {parseFloat(existingUnitPrice).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          : <span className="text-gray-400">calculated from cost &amp; markup</span>
                        }
                      </p>
                    </div>

                    {/* Batch */}
                    <div className="mb-4 pt-2 border-t border-gray-100 space-y-3">
                      {/* <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch</p> */}

                      {/* Batch Number — required */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Batch No. <span className="text-[#ed1438]">*</span>
                        </label>
                        <input
                          type="text"
                          value={batchNumber}
                          onChange={(e) => setBatchNumber(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        />
                      </div>

                      {/* Expiry Date — optional */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date <span className="text-xs font-normal text-gray-500">(optional)</span>
                        </label>
                        <input
                          type="date"
                          value={expiryDate}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        />
                      </div>
                    </div>

                    {/* Supplier */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier <span className="text-xs font-normal text-gray-500">(optional)</span>
                      </label>
                      {selectedSupplier ? (
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#84d0f4]/10 border border-[#50b3e1]/30 rounded-lg">
                          <i className="ri-truck-line text-[#0997a4]"></i>
                          <span className="flex-1 text-sm font-medium text-gray-900">{selectedSupplier.name}</span>
                          {selectedSupplier.phone && <span className="text-xs text-gray-500">{selectedSupplier.phone}</span>}
                          <button type="button" onClick={() => { setSelectedSupplier(null); setSupplierSearch(''); }} className="text-gray-400 hover:text-gray-600">
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="relative">
                            <input
                              type="text"
                              value={supplierSearch}
                              onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); setShowAddSupplier(false); }}
                              onFocus={() => setShowSupplierDropdown(true)}
                              onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                              placeholder="Search suppliers..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            />
                            {isSearchingSupplier && (
                              <div className="absolute right-3 top-2.5">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#50b3e1] border-t-transparent"></div>
                              </div>
                            )}
                          </div>
                          {showSupplierDropdown && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {supplierResults.length > 0 ? supplierResults.map(s => (
                                <button key={s.id} type="button" onMouseDown={() => handleSelectSupplier(s)} className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between">
                                  <span className="font-medium text-gray-900">{s.name}</span>
                                  {s.phone && <span className="text-xs text-gray-500">{s.phone}</span>}
                                </button>
                              )) : supplierSearch.trim() ? (
                                <div className="p-2">
                                  <p className="px-2 py-1 text-xs text-gray-500">No suppliers found</p>
                                  <button
                                    type="button"
                                    onMouseDown={() => { setNewSupplierName(supplierSearch.trim()); setShowAddSupplier(true); setShowSupplierDropdown(false); }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#0997a4] hover:bg-[#50b3e1]/10 flex items-center gap-2 font-medium"
                                  >
                                    <i className="ri-add-line"></i>
                                    Add "{supplierSearch.trim()}"
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}
                      {showAddSupplier && (
                        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                          <p className="text-xs font-semibold text-gray-700">New Supplier</p>
                          <input type="text" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} placeholder="Name *" className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                          <div className="grid grid-cols-2 gap-2">
                            <input type="tel" value={newSupplierPhone} onChange={(e) => setNewSupplierPhone(e.target.value)} placeholder="Phone" className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                            <input type="email" value={newSupplierEmail} onChange={(e) => setNewSupplierEmail(e.target.value)} placeholder="Email" className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                          </div>
                          <div className="flex gap-2">
                            <button type="button" onClick={handleCreateSupplier} disabled={!newSupplierName.trim()} className="flex-1 px-3 py-1.5 bg-[#0997a4] text-white rounded text-sm font-medium hover:bg-[#087d89] disabled:opacity-50 disabled:cursor-not-allowed">
                              Save Supplier
                            </button>
                            <button type="button" onClick={() => { setShowAddSupplier(false); setNewSupplierName(''); setNewSupplierPhone(''); setNewSupplierEmail(''); }} className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Location */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Storage Location <span className="text-[#ed1438]">*</span></label>
                      <select
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}
                        disabled={loadingData}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{loadingData ? 'Loading locations...' : 'Select location...'}</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={handleAddItem}
                      className="w-full px-4 py-3 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line mr-2"></i>
                      {isNewProduct ? 'Add New Product to Receipt' : 'Add to Receipt'}
                    </button>
                  </>
                ) : (
                  <>
                    <ProductForm
                      productName={newProductName}
                      onProductNameChange={setNewProductName}
                      categoryId={newProductCategoryId}
                      onCategoryIdChange={setNewProductCategoryId}
                      unit={newProductUnit}
                      onUnitChange={setNewProductUnit}
                      unitPrice={newProductUnitPrice}
                      onUnitPriceChange={setNewProductUnitPrice}
                      costPrice={newProductCostPrice}
                      onCostPriceChange={setNewProductCostPrice}
                      markupType={newProductMarkupType}
                      onMarkupTypeChange={setNewProductMarkupType}
                      markupValue={newProductMarkupValue}
                      onMarkupValueChange={setNewProductMarkupValue}
                      minQuantity={newProductMinQuantity}
                      onMinQuantityChange={setNewProductMinQuantity}
                      quantity={quantity}
                      onQuantityChange={setQuantity}
                      itemsPerUnit={itemsPerUnit}
                      onItemsPerUnitChange={setItemsPerUnit}
                      categories={categories}
                      units={units}
                      loadingData={loadingData}
                    />
                    
                    {/* Barcode Display - Shows if barcode exists (from search) */}
                    {selectedItem.barcode && !generatedBarcode && (
                      <div className="p-3 bg-gray-50 rounded-lg mb-4">
                        <p className="text-xs text-gray-600">Barcode</p>
                        <p className="text-sm font-mono font-medium text-gray-900">{selectedItem.barcode}</p>
                      </div>
                    )}

                    {/* Batch - For new products */}
                    <div className="mb-4 pt-2 border-t border-gray-100 space-y-3">
                      {/* <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch Tracking</p> */}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Batch No. <span className="text-[#ed1438]">*</span>
                        </label>
                        <input
                          type="text"
                          value={batchNumber}
                          onChange={(e) => setBatchNumber(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date <span className="text-xs font-normal text-gray-500">(optional)</span>
                        </label>
                        <input
                          type="date"
                          value={expiryDate}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        />
                      </div>
                    </div>

                    {/* Supplier - For new products */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier <span className="text-xs font-normal text-gray-500">(optional)</span>
                      </label>
                      {selectedSupplier ? (
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#84d0f4]/10 border border-[#50b3e1]/30 rounded-lg">
                          <i className="ri-truck-line text-[#0997a4]"></i>
                          <span className="flex-1 text-sm font-medium text-gray-900">{selectedSupplier.name}</span>
                          {selectedSupplier.phone && <span className="text-xs text-gray-500">{selectedSupplier.phone}</span>}
                          <button type="button" onClick={() => { setSelectedSupplier(null); setSupplierSearch(''); }} className="text-gray-400 hover:text-gray-600">
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="relative">
                            <input
                              type="text"
                              value={supplierSearch}
                              onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); setShowAddSupplier(false); }}
                              onFocus={() => setShowSupplierDropdown(true)}
                              onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                              placeholder="Search suppliers..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                            />
                            {isSearchingSupplier && (
                              <div className="absolute right-3 top-2.5">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#50b3e1] border-t-transparent"></div>
                              </div>
                            )}
                          </div>
                          {showSupplierDropdown && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {supplierResults.length > 0 ? supplierResults.map(s => (
                                <button key={s.id} type="button" onMouseDown={() => handleSelectSupplier(s)} className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between">
                                  <span className="font-medium text-gray-900">{s.name}</span>
                                  {s.phone && <span className="text-xs text-gray-500">{s.phone}</span>}
                                </button>
                              )) : supplierSearch.trim() ? (
                                <div className="p-2">
                                  <p className="px-2 py-1 text-xs text-gray-500">No suppliers found</p>
                                  <button
                                    type="button"
                                    onMouseDown={() => { setNewSupplierName(supplierSearch.trim()); setShowAddSupplier(true); setShowSupplierDropdown(false); }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#0997a4] hover:bg-[#50b3e1]/10 flex items-center gap-2 font-medium"
                                  >
                                    <i className="ri-add-line"></i>
                                    Add "{supplierSearch.trim()}"
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}
                      {showAddSupplier && (
                        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                          <p className="text-xs font-semibold text-gray-700">New Supplier</p>
                          <input type="text" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} placeholder="Name *" className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                          <div className="grid grid-cols-2 gap-2">
                            <input type="tel" value={newSupplierPhone} onChange={(e) => setNewSupplierPhone(e.target.value)} placeholder="Phone" className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                            <input type="email" value={newSupplierEmail} onChange={(e) => setNewSupplierEmail(e.target.value)} placeholder="Email" className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]" />
                          </div>
                          <div className="flex gap-2">
                            <button type="button" onClick={handleCreateSupplier} disabled={!newSupplierName.trim()} className="flex-1 px-3 py-1.5 bg-[#0997a4] text-white rounded text-sm font-medium hover:bg-[#087d89] disabled:opacity-50 disabled:cursor-not-allowed">
                              Save Supplier
                            </button>
                            <button type="button" onClick={() => { setShowAddSupplier(false); setNewSupplierName(''); setNewSupplierPhone(''); setNewSupplierEmail(''); }} className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Location - For new products */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Storage Location <span className="text-[#ed1438]">*</span></label>
                      <select
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}
                        disabled={loadingData}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{loadingData ? 'Loading locations...' : 'Select location...'}</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={handleAddItem}
                      className="w-full px-4 py-3 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Add New Product to Receipt
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Receipt Summary */}
          {receivedItems.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="text-center py-12">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500">No items added yet</p>
              </div>
            </div>
          ) : (
            <ReceiptSummary
              items={receivedItems}
              onRemoveItem={(index) => {
                setReceivedItems(receivedItems.filter((_, i) => i !== index));
              }}
              onSubmit={handleSubmit}
              notes={notes}
              onNotesChange={setNotes}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <ScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
        error={scannerError}
      />
      </DashboardLayout>
    </>
  );
}
